import { useMemo } from "react";

import { useEvmBackend } from "../context";

import type { Address } from "viem";

interface ResolvedAddress {
  label: string;
  route: string;
}

/**
 * Resolves a hex address to a human-readable label and a navigation route.
 * Checks pre-funded accounts, deployed contracts, and mock tokens.
 */
export function useAddressResolver(): {
  resolveAddress: (addr: Address) => ResolvedAddress | null;
  labelMap: Record<string, string>;
} {
  const { deployment, accounts } = useEvmBackend();

  const map = useMemo(() => {
    const m = new Map<string, ResolvedAddress>();

    // Pre-funded accounts
    for (const acct of accounts) {
      m.set(acct.address.toLowerCase(), {
        label: acct.label,
        route: "/keys",
      });
    }

    if (!deployment) return m;

    // Tokens
    if (deployment.tokens) {
      const tokenEntries: [string, string][] = [
        [deployment.tokens.weth, "WETH"],
        [deployment.tokens.usdc, "USDC"],
        [deployment.tokens.usdt, "USDT"],
        [deployment.tokens.dai, "DAI"],
        [deployment.tokens.wbtc, "WBTC"],
      ];
      for (const [addr, symbol] of tokenEntries) {
        if (addr) {
          m.set(addr.toLowerCase(), { label: symbol, route: "/balances" });
        }
      }
    }

    // Foundation
    m.set(deployment.foundation.weth9.toLowerCase(), {
      label: "WETH9",
      route: "/contracts",
    });
    m.set(deployment.foundation.permit2.toLowerCase(), {
      label: "Permit2",
      route: "/contracts",
    });

    // V2
    m.set(deployment.v2.factory.toLowerCase(), {
      label: "V2 Factory",
      route: "/contracts",
    });
    m.set(deployment.v2.router02.toLowerCase(), {
      label: "V2 Router",
      route: "/contracts",
    });

    // V3
    m.set(deployment.v3.factory.toLowerCase(), {
      label: "V3 Factory",
      route: "/contracts",
    });
    m.set(deployment.v3.router.toLowerCase(), {
      label: "V3 Router",
      route: "/contracts",
    });
    m.set(deployment.v3.nonfungiblePositionManager.toLowerCase(), {
      label: "V3 NFT PM",
      route: "/contracts",
    });
    m.set(deployment.v3.quoterV2.toLowerCase(), {
      label: "V3 Quoter",
      route: "/contracts",
    });

    // V4
    m.set(deployment.v4.poolManager.toLowerCase(), {
      label: "V4 PoolManager",
      route: "/contracts",
    });
    m.set(deployment.v4.positionManager.toLowerCase(), {
      label: "V4 PositionMgr",
      route: "/contracts",
    });

    // Universal Router
    m.set(deployment.universalRouter.router.toLowerCase(), {
      label: "Universal Router",
      route: "/contracts",
    });

    // UniswapX
    m.set(deployment.uniswapX.exclusiveDutchOrderReactor.toLowerCase(), {
      label: "Dutch Reactor",
      route: "/contracts",
    });

    // ERC-8004
    m.set(deployment.erc8004.identityRegistry.toLowerCase(), {
      label: "Identity Registry",
      route: "/contracts",
    });
    m.set(deployment.erc8004.reputationRegistry.toLowerCase(), {
      label: "Reputation Registry",
      route: "/contracts",
    });

    // Vault
    if (deployment.vault) {
      m.set(deployment.vault.vaultFactory.toLowerCase(), {
        label: "Vault Factory",
        route: "/contracts",
      });
      m.set(deployment.vault.feeModule.toLowerCase(), {
        label: "Fee Module",
        route: "/contracts",
      });
      m.set(deployment.vault.identityAdapter.toLowerCase(), {
        label: "Identity Adapter",
        route: "/contracts",
      });
      m.set(deployment.vault.vaultImplementation.toLowerCase(), {
        label: "Vault Impl",
        route: "/contracts",
      });
    }

    return m;
  }, [deployment, accounts]);

  const labelMap = useMemo(() => {
    const result: Record<string, string> = {};
    for (const [addr, resolved] of map) {
      result[addr] = resolved.label;
    }
    return result;
  }, [map]);

  return {
    resolveAddress: (addr: Address) => map.get(addr.toLowerCase()) ?? null,
    labelMap,
  };
}
